/**
 * HypothesisField — Puente entre la hipótesis diagnóstica del alumno y el
 * estado visual-físico del grafo.
 *
 * Escucha HYPOTHESIS_SET del bus. Cuando el alumno (o el diferencial) cambia
 * de dx líder, este módulo:
 *   1. Traduce dxName → mapa de deltas usando DIFFERENTIAL_CASES.
 *   2. Activa el campo de fuerzas en MedicalPhysics (atrae evidencia a favor,
 *      repele evidencia en contra, más fuerte si está confirmado).
 *   3. Pulsa visualmente los nodos de apoyo visibles.
 *
 * Sin esto, la hipótesis era un número en un panel. Con esto, el grafo se
 * reconfigura alrededor de la sospecha clínica del alumno.
 */

import state from '../core/state';
import { MedicalPhysics } from '../physics/MedicalPhysics';
import { Patient } from './Patient';
import { DIFFERENTIAL_CASES } from '../education/differential-data';

export class HypothesisField {
  private unsubscribe: (() => void) | null = null;
  private physics: MedicalPhysics | null = null;
  private patient: Patient | null = null;

  start(): void {
    this.stop();
    this.unsubscribe = state.onEvent('HYPOTHESIS_SET', (e) => {
      if (e.type !== 'HYPOTHESIS_SET') return;
      this.apply(e.caseId, e.dxName, e.confirmed);
    });
  }

  stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /**
   * Asocia las referencias de runtime. Se llama en cada loadPatient() porque
   * MedicalPhysics y Patient se recrean al cambiar de caso.
   */
  bind(physics: MedicalPhysics | null, patient: Patient | null): void {
    this.physics = physics;
    this.patient = patient;
    // Al cambiar de caso, limpia el campo previo — la hipótesis anterior
    // pertenece a otro paciente.
    this.physics?.clearActiveHypothesis();
  }

  private apply(caseId: string, dxName: string, confirmed: boolean): void {
    if (!this.physics || !this.patient) return;

    const config = DIFFERENTIAL_CASES[caseId];
    const dx = config?.differentials.find(d => d.name === dxName);
    if (!dx) {
      this.physics.clearActiveHypothesis();
      return;
    }

    const deltas = new Map<string, number>();
    Object.entries(dx.findings).forEach(([nodeId, delta]) => {
      if (delta !== 0) deltas.set(nodeId, delta);
    });

    this.physics.setActiveHypothesis(deltas, confirmed);

    // Pulso visual solo para evidencia a favor y que ya fue revelada, para no
    // delatar hallazgos aún no ordenados.
    deltas.forEach((delta, nodeId) => {
      if (delta <= 0) return;
      const node = this.patient!.getNode(nodeId);
      if (node?.visible) node.pulse();
    });
  }
}

export default HypothesisField;
